/**
 * Media transport adapter: the only module that knows how prepared bytes reach
 * the `<video>` element.
 *
 * The backend implements `progressive-mp4` exclusively (see
 * docs/decisions/001-media-delivery.md): a complete, server-side remuxed MP4
 * served with HTTP Range support, so the browser's native demuxer and seeking
 * do all the work. `hls` is part of the wire protocol but not implemented in
 * this build; it is reported as unsupported instead of failing obscurely.
 *
 * Extension point: to add HLS later, add a `case 'hls'` branch below that
 * attaches a maintained HLS client (e.g. hls.js) to the same element and
 * returns the same `MediaAttachment` interface, and add `'hls'` to
 * `SUPPORTED_TRANSPORTS`. Nothing outside this file changes — the synchronized
 * player deliberately depends on that isolation.
 *
 * `playback_url` and every `SubtitleTrack.url` are opaque same-origin paths
 * issued by the server for one `MediaGeneration`. They are used verbatim and
 * never annotated with `crossorigin`: the session cookie must ride along, and
 * an anonymous CORS request would be rejected with 401.
 */

import type { ActiveMedia, MediaGeneration, MediaTransport, SubtitleTrack } from '../generated';

/** Transports this build can actually play. */
const SUPPORTED_TRANSPORTS: readonly MediaTransport[] = ['progressive-mp4'];

/** Live attachment of one prepared asset to one `<video>` element. */
export interface MediaAttachment {
  readonly transport: MediaTransport;
  /** Generation this attachment belongs to; events for other generations are stale. */
  readonly generation: MediaGeneration;
  /** Tracks attached as `<track>` children, in the order given by the server. */
  readonly subtitles: readonly SubtitleTrack[];
  /** `null` turns subtitles off; otherwise the index into `subtitles`. */
  selectSubtitle: (index: number | null) => void;
  /** Detaches source and subtitle tracks, leaving the element reusable. */
  detach: () => void;
}

/**
 * `null` when the transport is playable by this build, otherwise a
 * user-facing explanation. The UI calls this before attaching so an
 * unsupported transport is a clear message rather than a player error.
 */
export function unsupportedTransportMessage(transport: MediaTransport): string | null {
  if (SUPPORTED_TRANSPORTS.includes(transport)) return null;
  return (
    `The server prepared this video for the "${transport}" transport, which is ` +
    'unsupported in this build. Only progressive MP4 playback is implemented — ' +
    'no playback was started.'
  );
}

/** Removes any previously attached source and `<track>` children. */
function clearElement(video: HTMLVideoElement): void {
  video.pause();
  for (const track of Array.from(video.querySelectorAll('track'))) {
    track.remove();
  }
  video.removeAttribute('src');
  // Forces the element to drop the old resource instead of keeping the last
  // frame and buffered ranges around for the next source.
  video.load();
}

function attachProgressiveMp4(video: HTMLVideoElement, media: ActiveMedia): MediaAttachment {
  clearElement(video);

  const elements = media.subtitles.map((subtitle) => {
    const element = document.createElement('track');
    element.kind = 'subtitles';
    element.label = subtitle.auto_generated ? `${subtitle.label} (auto)` : subtitle.label;
    element.srclang = subtitle.language;
    // Same-origin VTT path issued by the server for this generation; used verbatim.
    element.src = subtitle.url;
    video.append(element);
    return element;
  });

  // `playback_url` is the server's own media path, used verbatim.
  video.src = media.playback_url;
  video.load();

  return {
    transport: 'progressive-mp4',
    generation: media.generation,
    subtitles: media.subtitles,
    selectSubtitle: (index) => {
      elements.forEach((element, position) => {
        // Firefox needs the track element to have started loading before its
        // mode sticks; assigning mode triggers that load.
        element.track.mode = position === index ? 'showing' : 'disabled';
      });
    },
    detach: () => clearElement(video),
  };
}

/**
 * Attaches the room's active media. Returns `null` only for transports this
 * build does not implement — callers must have shown
 * `unsupportedTransportMessage` first.
 */
export function attachActiveMedia(
  video: HTMLVideoElement,
  media: ActiveMedia,
): MediaAttachment | null {
  switch (media.transport) {
    case 'progressive-mp4':
      return attachProgressiveMp4(video, media);
    case 'hls':
      return null;
  }
}

/** Reason text for a `<video>` `error` event, mapped from MediaError codes. */
export function mediaErrorMessage(error: MediaError | null): string {
  switch (error?.code) {
    case MediaError.MEDIA_ERR_ABORTED:
      return 'Playback was aborted before the video could start.';
    case MediaError.MEDIA_ERR_NETWORK:
      return 'The browser lost the connection to the media file while playing.';
    case MediaError.MEDIA_ERR_DECODE:
      return 'The browser could not decode the prepared file (codec or container problem).';
    case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
      return 'The browser refused the prepared file: its codecs are unsupported here.';
    default:
      return 'The browser reported an unknown playback error.';
  }
}
